import React, { useEffect, useRef } from "react";
import { Bike, Bluetooth, BluetoothOff, Sailboat } from "lucide-react";
import PowerChart from "../PowerChart";
import { useZoneFloors } from "../../../contexts/ZoneFloorsContext";
import { zoneColourFor } from "../../../utils/heartRateZones";

const fmtInt = (v) => (v == null ? "—" : String(Math.round(v)));

/**
 * The bike or rower beside the ring when the phone is on its side (#149):
 * power, cadence (or stroke rate) and heart rate in a row over the last
 * minute's chart. Replaces swiping across to the full dashboard, which a
 * phone mounted on a machine makes awkward. Values are painted straight into
 * the DOM, like the full dashboards, so 10 Hz packets don't re-render.
 */
const EquipmentSidePanel = ({ device, hr, hrBpm, onAddDevice }) => {
  const powerRef = useRef(null);
  const rateRef = useRef(null);
  const hrRef = useRef(null);
  const floors = useZoneFloors();
  const floorsRef = useRef(floors);
  floorsRef.current = floors;
  const rower = device?.type === "rower";
  const subscribe = device?.subscribe;
  const metricsRef = device?.metricsRef;

  useEffect(() => {
    const paint = () => {
      const m = metricsRef?.current;
      if (powerRef.current) powerRef.current.textContent = fmtInt(m?.power_watts);
      if (rateRef.current) rateRef.current.textContent = fmtInt(rower ? m?.stroke_rate_spm : m?.cadence_rpm);
    };
    paint();
    const unsubscribe = subscribe?.(paint);
    return () => unsubscribe?.();
  }, [subscribe, metricsRef, rower]);

  const hrSubscribe = hr?.subscribe;
  const getFreshSnapshot = hr?.getFreshSnapshot;
  useEffect(() => {
    const paint = () => {
      if (!hrRef.current) return;
      const v = getFreshSnapshot?.(3000)?.bpm ?? hrBpm;
      hrRef.current.textContent = fmtInt(v);
      hrRef.current.style.color = v != null && floorsRef.current ? zoneColourFor(v, floorsRef.current) : "";
    };
    paint();
    const unsubscribe = hrSubscribe?.(paint);
    return () => unsubscribe?.();
  }, [hrSubscribe, getFreshSnapshot, hrBpm]);

  if (!device?.type) {
    return onAddDevice ? (
      <button
        type="button"
        onClick={onAddDevice}
        className="flex items-center justify-center gap-2 rounded-xl border border-border px-3 py-2 text-sm text-muted-foreground hover:text-foreground"
      >
        <Bluetooth className="h-4 w-4" />
        Connect equipment
      </button>
    ) : null;
  }

  const connected = device.status === "connected";
  const showHr = !!hr || hrBpm != null;
  const Icon = rower ? Sailboat : Bike;
  const stats = [
    { ref: powerRef, unit: "W", className: "text-yellow-500 dark:text-yellow-300" },
    { ref: rateRef, unit: rower ? "spm" : "rpm", className: "text-foreground" },
    ...(showHr ? [{ ref: hrRef, unit: "bpm", className: "text-red-400" }] : []),
  ];

  return (
    <section aria-label={rower ? "Rower" : "Bike"} className="flex min-h-0 flex-col gap-1.5">
      <header className="flex items-center justify-between gap-2 text-xs text-muted-foreground">
        <span className="flex items-center gap-1.5 uppercase tracking-wide">
          <Icon className="h-3.5 w-3.5" />
          {rower ? "Rower" : "Bike"}
        </span>
        <span className="flex min-w-0 items-center gap-1">
          {connected ? (
            <Bluetooth className="h-3 w-3 shrink-0 text-blue-400" />
          ) : (
            <BluetoothOff className="h-3 w-3 shrink-0" />
          )}
          <span className="truncate">
            {connected ? device.name || "Connected" : device.status === "reconnecting" ? "Reconnecting…" : "Not connected"}
          </span>
        </span>
      </header>
      <div className="grid gap-2" style={{ gridTemplateColumns: `repeat(${stats.length}, minmax(0, 1fr))` }}>
        {stats.map((s) => (
          <div key={s.unit} className="flex flex-col items-center rounded-lg border border-border/60 bg-muted/30 py-1">
            <span ref={s.ref} className={`font-mono text-xl font-bold tabular-nums leading-none ${s.className}`}>
              —
            </span>
            <span className="mt-0.5 text-[10px] uppercase tracking-widest text-muted-foreground">{s.unit}</span>
          </div>
        ))}
      </div>
      <div className="h-20 w-full overflow-hidden rounded-lg border border-border/60 bg-muted/20">
        <PowerChart subscribe={subscribe} hr={hr} hrBpm={hrBpm} className="block h-full w-full" />
      </div>
    </section>
  );
};

export default EquipmentSidePanel;
